/**
 * ICONOS SVG PROPIOS.
 *
 * SIN EMOJIS en ninguna parte de la interfaz. Un emoji se ve distinto en cada
 * sistema, no hereda el color del texto y no se puede etiquetar para un lector
 * de pantalla. Estos trazos heredan `currentColor` y llevan `aria-hidden`
 * porque el significado va SIEMPRE en el texto que los acompana.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 20, children, ...props }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconImage = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M21 15l-5-5-6 6-2-2-5 5" />
  </Icon>
);

export const IconText = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 6h14M12 6v13M9 19h6" />
  </Icon>
);

export const IconShape = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="4" width="10" height="10" rx="1" />
    <circle cx="15.5" cy="15.5" r="4.5" />
  </Icon>
);

export const IconLayers = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3l9 5-9 5-9-5 9-5z" />
    <path d="M3 13l9 5 9-5" />
  </Icon>
);

export const IconTrash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    <path d="M10 11v6M14 11v6" />
  </Icon>
);

export const IconLock = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </Icon>
);

export const IconUnlock = (p: IconProps) => (
  <Icon {...p}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 017.5-2" />
  </Icon>
);

export const IconUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Icon>
);

export const IconDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Icon>
);

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 12.5l4.5 4.5L19 7" />
  </Icon>
);

export const IconAlert = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4l9 16H3l9-16z" />
    <path d="M12 10v4M12 17.2v.1" />
  </Icon>
);

export const IconInfo = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 7.8v.1" />
  </Icon>
);

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Icon>
);

export const IconBack = (p: IconProps) => (
  <Icon {...p}>
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </Icon>
);

export const IconCart = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 4h2l2.4 11.2a2 2 0 002 1.6h7.4a2 2 0 002-1.6L20.5 8H6" />
    <circle cx="9.5" cy="20" r="1.2" />
    <circle cx="17.5" cy="20" r="1.2" />
  </Icon>
);

export const IconWallet = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18M16.5 14.5h.01" />
  </Icon>
);

export const IconUser = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 20a7 7 0 0114 0" />
  </Icon>
);

export const IconGrid = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </Icon>
);

export const IconRuler = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.5" y="8" width="19" height="8" rx="1.5" />
    <path d="M7 8v3M11 8v4M15 8v3M19 8v4" />
  </Icon>
);

export const IconTarget = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.5" />
  </Icon>
);

export const IconSpinner = ({ size = 20, ...props }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className="animate-spin"
    {...props}
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
    <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Monograma de Besbello: dos "b" enfrentadas. Sustituible por el oficial. */
export const BrandMark = ({ size = 28, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false" {...props}>
    <rect width="32" height="32" rx="8" fill="currentColor" />
    <path
      d="M9 8v16M9 13h4a3.5 3.5 0 010 7H9M23 8v16M23 13h-4a3.5 3.5 0 000 7h4"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/** Deshacer: flecha que vuelve sobre sus pasos. Sin emojis, como todo aquí. */
export const IconUndo = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
  </Icon>
);

export const IconRedo = (p: IconProps) => (
  <Icon {...p}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H10a6 6 0 0 0 0 12h3" />
  </Icon>
);

/** Alineación: tres barras contra un eje. */
export const IconAlignLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4v16" />
    <rect x="7" y="6.5" width="12" height="3.5" rx="0.8" />
    <rect x="7" y="14" width="8" height="3.5" rx="0.8" />
  </Icon>
);

export const IconAlignCenter = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4v16" />
    <rect x="5" y="6.5" width="14" height="3.5" rx="0.8" />
    <rect x="8" y="14" width="8" height="3.5" rx="0.8" />
  </Icon>
);

export const IconAlignRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 4v16" />
    <rect x="5" y="6.5" width="12" height="3.5" rx="0.8" />
    <rect x="9" y="14" width="8" height="3.5" rx="0.8" />
  </Icon>
);

/*
  ALINEADO EN VERTICAL Y REPARTO.

  Mismo lenguaje que los tres de arriba: un eje sólido contra el que se apoyan
  las piezas. Girados 90 grados para el eje horizontal.
*/
export const IconAlignTop = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4h16" />
    <rect x="6.5" y="7" width="3.5" height="12" rx="0.8" />
    <rect x="14" y="7" width="3.5" height="8" rx="0.8" />
  </Icon>
);

export const IconAlignMiddle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 12h16" />
    <rect x="6.5" y="5" width="3.5" height="14" rx="0.8" />
    <rect x="14" y="8" width="3.5" height="8" rx="0.8" />
  </Icon>
);

export const IconAlignBottom = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20h16" />
    <rect x="6.5" y="5" width="3.5" height="12" rx="0.8" />
    <rect x="14" y="9" width="3.5" height="8" rx="0.8" />
  </Icon>
);

/** Repartir: tres piezas y los huecos iguales entre ellas. */
export const IconDistributeH = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="7" width="3.5" height="10" rx="0.8" />
    <rect x="10.25" y="7" width="3.5" height="10" rx="0.8" />
    <rect x="17.5" y="7" width="3.5" height="10" rx="0.8" />
  </Icon>
);

export const IconDistributeV = (p: IconProps) => (
  <Icon {...p}>
    <rect x="7" y="3" width="10" height="3.5" rx="0.8" />
    <rect x="7" y="10.25" width="10" height="3.5" rx="0.8" />
    <rect x="7" y="17.5" width="10" height="3.5" rx="0.8" />
  </Icon>
);

/** Zoom: la lupa con el signo dentro. */
export const IconZoomIn = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m20 20-4.4-4.4" />
    <path d="M10.5 7.5v6M7.5 10.5h6" />
  </Icon>
);

export const IconZoomOut = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m20 20-4.4-4.4" />
    <path d="M7.5 10.5h6" />
  </Icon>
);

/** Ajustar a la zona: cuatro esquinas apuntando hacia dentro. */
export const IconZoomFit = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 9V4h5" />
    <path d="M20 9V4h-5" />
    <path d="M4 15v5h5" />
    <path d="M20 15v5h-5" />
  </Icon>
);

/** Quitar el fondo: una goma que borra hacia atrás. */
export const IconEraser = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.5 20H20" />
    <path d="m14 6-8 8a2 2 0 0 0 0 2.8l3.2 3.2h4l7.3-7.3a2 2 0 0 0 0-2.8L17.3 6a2 2 0 0 0-2.8 0Z" />
    <path d="m10.5 9.5 5.5 5.5" />
  </Icon>
);

/** Vectorizar: nodos y una curva entre ellos, que es lo que sale. */
export const IconVector = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="3" width="4" height="4" rx="0.5" />
    <rect x="17" y="17" width="4" height="4" rx="0.5" />
    <path d="M7 5h6a6 6 0 0 1 6 6v6" />
  </Icon>
);

/** Plantillas: una rejilla de bloques, como una composición ya montada. */
export const IconTemplate = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M10 9v12" />
  </Icon>
);

/** Agrupar: dos piezas dentro de un marco de esquinas. */
export const IconGroup = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 7V4h3M21 7V4h-3M3 17v3h3M21 17v3h-3" />
    <rect x="7" y="7" width="5" height="5" rx="0.5" />
    <rect x="12" y="12" width="5" height="5" rx="0.5" />
  </Icon>
);

/** Desagrupar: el marco roto y las piezas sueltas. */
export const IconUngroup = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" />
  </Icon>
);

/** Copiar: dos hojas, una detrás de la otra. */
export const IconCopy = (p: IconProps) => (
  <Icon {...p}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </Icon>
);
